import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Plus,
  AlertCircle,
  Phone,
  FileText,
  Wrench,
  CheckCircle2,
  MapPin,
  Globe,
  CalendarDays,
  Clock,
  ClipboardList,
} from "lucide-react";
import {
  getBusiness,
  updateBusiness,
  listPhoneNumbers,
  createPhoneNumber,
  listBusinessTools,
  listTools,
  addBusinessTool,
  updateBusinessTool,
  deleteBusinessTool,
  getClient,
  getActiveBusinessFlow,
  publishBusinessFlow,
} from "../../../api/chatbotApi";
import { fetchTenant } from "../../../api/tenantApi";
import SchedulingTab from "../../../components/admin/scheduling/SchedulingTab";
import SchedulesPanel from "../../../components/admin/notifications/SchedulesPanel";
import MessageIdentitySettings from "../../../components/admin/MessageIdentitySettings";
import ConsentFormSettings from "../../../components/admin/ConsentFormSettings";
import { BasicSettings } from "../../../components/admin/TenantConfigTab";
import { useBotWorkspace } from "../../../components/admin/BotWorkspace";
import TriggerSettings from "../../../components/admin/notifications/TriggerSettings";
import WhatsAppTemplatesPanel from "../../../components/admin/notifications/WhatsAppTemplatesPanel";
import WhatsAppNotificationsPanel from "../../../components/admin/notifications/WhatsAppNotificationsPanel";
import useAuth from "../../../hooks/useAuth";
import { can, isClientAdmin } from "../../../util/accessControl";
import { agentConfigTabAccess } from "../../../util/routeAccess";
import ChatbotFormsPage from "./ChatbotFormsPage";

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        const field = Array.isArray(item?.loc) ? item.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${item?.msg || "Invalid value"}` : item?.msg || "Invalid value";
      })
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || fallback;
  return fallback;
};

export default function ChatbotBusinessDetailPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const canManageTemplates = can(user, "whatsapp_templates.read");
  const canReadConfig = can(user, "bot_configuration.read");
  const canUpdateConfig = can(user, "bot_configuration.update");
  const canManageTools = isClientAdmin(user);
  const canReadScheduling = can(user, "appointment_configuration.read");
  const canReadSchedules = can(user, "schedules.read");
  const canReadNotificationConfig = can(user, "notification_configuration.read");
  const [schedulerSection, setSchedulerSection] = useState("schedules");
  const { businessId } = useParams();
  const workspace = useBotWorkspace();
  const canManageTriggers = isSuperAdmin;
  const [business, setBusiness] = useState(null);
  const [clientName, setClientName] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const tabAccess = agentConfigTabAccess(user);
  const selectedTab = requestedTab === "whatsapp" ? "whatsapp-templates"
    : requestedTab || Object.keys(tabAccess).find((tab) => tabAccess[tab]);
  const schedulingSection = searchParams.get("section");
  const sectionAccess = {
    appointments: can(user, "appointments.read") || canReadScheduling,
    providers: tabAccess.providers,
    services: tabAccess.services,
    settings: canReadScheduling,
    notifications: canReadNotificationConfig,
  };
  const activeTab = tabAccess[selectedTab] === true
    && (selectedTab !== "appointments" || !schedulingSection || sectionAccess[schedulingSection] === true)
    ? selectedTab : null;
  const setActiveTab = (tab) => setSearchParams(tab === "basic" ? {} : { tab });
  const activeSchedulingSection = ["appointments", "providers", "services", "settings", "notifications"].includes(schedulingSection)
    ? schedulingSection : "appointments";
  const [flowJson, setFlowJson] = useState("{}");
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowSaving, setFlowSaving] = useState(false);

  // Form Details (Enriched unified Tenant + Business fields)
  const [form, setForm] = useState({
    name: "",
    domain: "",
    system_prompt: "",
    industry: "",
    business_type: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    business_phone: "",
    business_email: "",
    business_website: "",
    business_address: "",
    country_code: "IN",
    country: "India",
    timezone: "Asia/Kolkata",
    status: "active",
    plan: "starter",
    whatsapp_display_name: "",
    business_category: "",
    metadata_json: "{}",
  });
  const [saving, setSaving] = useState(false);

  // Phone Numbers
  const [phones, setPhones] = useState([]);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneForm, setPhoneForm] = useState({ phone_number_id: "", display_phone_number: "", wa_access_token: "" });
  const [phoneSaving, setPhoneSaving] = useState(false);

  // Tools
  const [bizTools, setBizTools] = useState([]);
  const loadedBizToolsKey = useRef(null);
  const [allTools, setAllTools] = useState([]);
  const [showAddToolModal, setShowAddToolModal] = useState(false);
  const [addToolId, setAddToolId] = useState("");
  const [addToolConfig, setAddToolConfig] = useState("{}");
  const [addToolSaving, setAddToolSaving] = useState(false);

  // Edit Tool Modal
  const [editingTool, setEditingTool] = useState(null);
  const [editToolForm, setEditToolForm] = useState({ enabled: true, configuration: "{}" });
  const [editToolSaving, setEditToolSaving] = useState(false);

  const loadBusiness = async () => {
    setLoading(true);
    setBusiness(null);
    setError("");
    try {
      const b = !workspace || canReadConfig
        ? { ...workspace, ...await getBusiness(businessId) } : workspace;
      setBusiness(b);
      if (!workspace) {
        const client = await getClient(b.client_id);
        setClientName(client?.name || "");
      }
      if (!workspace && b.tenant_id) {
        const tenant = await fetchTenant(b.tenant_id);
        setTenantName(tenant?.name || "");
      }
      setForm({
        name: b.name || "",
        domain: b.domain || "",
        system_prompt: b.system_prompt || "",
        industry: b.industry || "",
        business_type: b.business_type || "",
        owner_name: b.owner_name || "",
        owner_email: b.owner_email || "",
        owner_phone: b.owner_phone || "",
        business_phone: b.business_phone || "",
        business_email: b.business_email || "",
        business_website: b.business_website || "",
        business_address: b.business_address || "",
        country_code: b.country_code || "IN",
        country: b.country || "India",
        timezone: b.timezone || "Asia/Kolkata",
        status: b.status || "active",
        plan: b.plan || "starter",
        whatsapp_display_name: b.whatsapp_display_name || "",
        business_category: b.business_category || "",
        metadata_json: JSON.stringify(b.metadata_json || {}, null, 2),
      });
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load business info"));
    } finally {
      setLoading(false);
    }
  };

  const loadPhones = () =>
    listPhoneNumbers(businessId)
      .then(setPhones)
      .catch(() => setError("Failed to load phone numbers"));

  const loadBizTools = useCallback(() => {
    if (!business?.client_id) return Promise.resolve();
    return listBusinessTools(businessId, business.client_id)
      .then(setBizTools)
      .catch(() => setError("Failed to load business tools"));
  }, [businessId, business?.client_id]);

  useEffect(() => {
    if (activeTab) loadBusiness();
  }, [businessId, Boolean(activeTab)]);

  useEffect(() => {
    if (activeTab === "phones") loadPhones();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "tools") {
      loadedBizToolsKey.current = null;
      return;
    }

    if (!business?.client_id) {
      setAllTools([]);
      setError("Unable to load tools because this bot is not linked to a client.");
      return;
    }

    const requestKey = `${businessId}:${business.client_id}`;
    if (loadedBizToolsKey.current === requestKey) return;
    loadedBizToolsKey.current = requestKey;

    loadBizTools().catch(() => {
      loadedBizToolsKey.current = null;
    });
    listTools(business.client_id)
      .then((response) => {
        const items = [response, response?.items, response?.tools, response?.data, response?.data?.items]
          .find(Array.isArray) || [];
        setAllTools(items);
      })
      .catch((err) => setError(getErrorMessage(err, "Failed to load available tools")));
  }, [activeTab, business?.client_id, businessId, loadBizTools]);

  useEffect(() => {
    if (activeTab !== "flow") return;

    const loadActiveFlow = async () => {
      setFlowLoading(true);
      setError("");
      try {
        const response = await getActiveBusinessFlow(businessId);
        const responseData = response?.data || response;
        const activeFlow = responseData?.flow_json || responseData;
        const flowConfig = activeFlow?.flow_json || activeFlow || {};
        setFlowJson(JSON.stringify(flowConfig, null, 2));
      } catch (err) {
        if (err.response?.status === 404 && err.response?.data?.detail === "No active flow config found.") {
          setFlowJson("{}");
        } else {
          setError(getErrorMessage(err, "Failed to load active bot flow"));
        }
      } finally {
        setFlowLoading(false);
      }
    };

    loadActiveFlow();
  }, [activeTab, businessId]);

  const handleSaveDetails = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMsg("");

    let parsedMeta = {};
    if (form.metadata_json && form.metadata_json.trim()) {
      try {
        parsedMeta = JSON.parse(form.metadata_json);
      } catch {
        setError("Invalid JSON in Metadata field");
        setSaving(false);
        return;
      }
    }

    try {
      const payload = {
        system_prompt: form.system_prompt,
        metadata_json: parsedMeta,
      };
      const updated = await updateBusiness(businessId, payload);
      setBusiness(updated);
      workspace?.updateBot(updated);
      setSuccessMsg("Bot configuration updated successfully!");
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      setError(getErrorMessage(e, "Error saving business details"));
    } finally {
      setSaving(false);
    }
  };

  const handlePublishFlow = async () => {
    setFlowSaving(true);
    setError("");
    setSuccessMsg("");

    let parsedFlow = {};
    if (flowJson.trim()) {
      try {
        parsedFlow = JSON.parse(flowJson);
      } catch {
        setError("Invalid JSON in Bot Flow Configuration");
        setFlowSaving(false);
        return;
      }
    }

    try {
      const flowConfig = parsedFlow?.flow_json && typeof parsedFlow.flow_json === "object"
        ? parsedFlow.flow_json
        : parsedFlow;
      await publishBusinessFlow(businessId, flowConfig);
      setSuccessMsg("Bot flow published successfully!");
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      setError(getErrorMessage(e, "Error publishing bot flow"));
    } finally {
      setFlowSaving(false);
    }
  };

  const handleAddPhone = async () => {
    setPhoneSaving(true);
    setError("");
    try {
      const payload = { ...phoneForm };
      if (!payload.display_phone_number) delete payload.display_phone_number;
      await createPhoneNumber(businessId, payload);
      setShowPhoneModal(false);
      setPhoneForm({ phone_number_id: "", display_phone_number: "", wa_access_token: "" });
      loadPhones();
      loadBusiness();
    } catch (e) {
      setError(getErrorMessage(e, "Error adding phone number"));
    } finally {
      setPhoneSaving(false);
    }
  };

  const handleAddTool = async () => {
    const selectedToolId = String(addToolId || "").trim();
    if (!selectedToolId) {
      setError("Please select a tool before linking it");
      return;
    }

    setAddToolSaving(true);
    setError("");
    try {
      let configuration = {};
      try {
        configuration = JSON.parse(addToolConfig);
      } catch {
        setError("Invalid JSON in configuration");
        setAddToolSaving(false);
        return;
      }
      await addBusinessTool(businessId, { tool_id: selectedToolId, enabled: true, configuration });
      setShowAddToolModal(false);
      setAddToolId("");
      setAddToolConfig("{}");
      loadBizTools();
    } catch (e) {
      setError(getErrorMessage(e, "Error linking tool"));
    } finally {
      setAddToolSaving(false);
    }
  };

  const openEditTool = (bt) => {
    setEditingTool(bt);
    setEditToolForm({
      enabled: bt.enabled,
      configuration: JSON.stringify(bt.configuration, null, 2),
    });
  };

  const handleSaveEditTool = async () => {
    setEditToolSaving(true);
    setError("");
    try {
      let configuration = {};
      try {
        configuration = JSON.parse(editToolForm.configuration);
      } catch {
        setError("Invalid JSON in configuration");
        setEditToolSaving(false);
        return;
      }
      await updateBusinessTool(businessId, editingTool.tool_id, { enabled: editToolForm.enabled, configuration });
      setEditingTool(null);
      loadBizTools();
    } catch (e) {
      setError(getErrorMessage(e, "Error updating tool configuration"));
    } finally {
      setEditToolSaving(false);
    }
  };

  const handleToggleTool = async (toolId, currentEnabled) => {
    try {
      await updateBusinessTool(businessId, toolId, { enabled: !currentEnabled });
      loadBizTools();
    } catch {
      setError("Error toggling tool state");
    }
  };

  const handleRemoveTool = async (toolId) => {
    if (!window.confirm("Remove this tool from the business?")) return;
    try {
      await deleteBusinessTool(businessId, toolId);
      loadBizTools();
    } catch {
      setError("Error removing tool");
    }
  };

  const unlinkedTools = allTools.filter((tool) => {
    const toolId = tool.id ?? tool.tool_id;
    return toolId != null && !bizTools.find((businessTool) => String(businessTool.tool_id) === String(toolId));
  });

  if (!activeTab) return <p role="alert">Access denied. You do not have permission to view this configuration.</p>;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-[#096dea]" />
          <span>Loading business information...</span>
        </div>
      </div>
    );
  }

  if (!business) {
    return <div className="space-y-3 p-8 text-center">
      <p role="alert" className="text-rose-700">{error || "Business unit not found."}</p>
      <button type="button" onClick={loadBusiness} className="text-sm font-semibold text-cyan-800">Retry loading configuration</button>
    </div>;
  }

  return (
    <div className="space-y-6">
      {/* Navigation Breadcrumb */}
      {!workspace && <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link
          to={`/clients/${business.client_id}`}
          className="hover:text-slate-900 transition flex items-center gap-1 font-medium"
        >
          <ArrowLeft size={16} /> Back to {clientName || "Client"}
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-900">{business.name}</span>
      </div>}

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle size={18} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMsg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle2 size={18} className="shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{business.name}</h1>
            {canReadConfig && <span className="inline-block uppercase tracking-wider rounded-md bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-700 border border-blue-100">
              {business.plan || "starter"}
            </span>}
          </div>
          {canReadConfig && <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-1.5">
            <span>
              Domain: <code className="bg-slate-100 px-2 py-0.5 rounded text-slate-800 font-bold">{business.domain}</code>
            </span>
            {!workspace && business.tenant_id && (
              <span>
                • Linked Tenant:{" "}
                <Link
                  to={`/agents/${business.tenant_id}`}
                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100 px-1.5 py-0.5 rounded text-[11px] font-semibold transition"
                  title="Open Tenant Details"
                >
                  {tenantName || "Tenant"} ↗
                </Link>
              </span>
            )}
            {business.whatsapp_phone_number && (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                <Phone size={11} /> {business.whatsapp_phone_number}
              </span>
            )}
          </div>}
        </div>
      </div>

      {/* Tabs Header */}
      <div className="flex flex-wrap border-b border-slate-200 gap-6">
        {canReadConfig && <button type="button" onClick={() => setActiveTab("basic")}
          aria-current={activeTab === "basic" ? "page" : undefined}
          className={`pb-3 text-sm font-semibold border-b-2 ${activeTab === "basic" ? "border-[#096dea] text-[#096dea]" : "border-transparent text-slate-500"}`}>
          Basic
        </button>}
        {canReadConfig && <button
          onClick={() => setActiveTab("details")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "details"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FileText size={16} /> System Prompt
        </button>}
        {canReadConfig && <button type="button" onClick={() => setActiveTab("chat-settings")}
          className={`pb-3 text-sm font-semibold border-b-2 ${activeTab === "chat-settings" ? "border-[#096dea] text-[#096dea]" : "border-transparent text-slate-500"}`}>
          Chat Settings
        </button>}
        {canManageTools && <button
          onClick={() => setActiveTab("tools")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "tools"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Wrench size={16} /> Tools ({bizTools.length})
        </button>}
        {canReadConfig && <button
          onClick={() => setActiveTab("flow")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "flow"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FileText size={16} /> Flow
        </button>}
        {["providers", "services"].filter((tab) => tabAccess[tab]).map((tab) => (
          <button key={tab} type="button" onClick={() => setActiveTab(tab)}
            aria-current={activeTab === tab ? "page" : undefined}
            className={`pb-3 text-sm font-semibold border-b-2 ${activeTab === tab ? "border-[#096dea] text-[#096dea]" : "border-transparent text-slate-500"}`}>
            {tab === "providers" ? "Providers" : "Services"}
          </button>
        ))}
        {canReadScheduling && <button
          onClick={() => setActiveTab("appointments")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "appointments"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <CalendarDays size={16} /> Appointments
        </button>}
        {canReadSchedules && <button
          onClick={() => setActiveTab("schedulers")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "schedulers"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Clock size={16} /> Schedulers
        </button>}
        {tabAccess.forms && <button
          onClick={() => setActiveTab("forms")}
          className={`pb-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
            activeTab === "forms"
              ? "border-[#096dea] text-[#096dea]"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <ClipboardList size={16} /> Forms
        </button>}
        {canManageTemplates && <button type="button" onClick={() => setActiveTab("whatsapp-templates")}
          className={`pb-3 text-sm font-semibold border-b-2 ${activeTab === "whatsapp-templates" ? "border-[#096dea] text-[#096dea]" : "border-transparent text-slate-500"}`}>
          WhatsApp Templates
        </button>}
      </div>

      {["providers", "services"].includes(activeTab) && <SchedulingTab key={activeTab}
        botId={businessId} activeSection={activeTab} hideNavigation />}
      {activeTab === "basic" && <BasicSettings key={businessId} botId={businessId} disabled={!canUpdateConfig} />}
      {activeTab === "whatsapp-templates" && canManageTemplates && <WhatsAppTemplatesPanel botId={businessId} />}
      {activeTab === "forms" && <ChatbotFormsPage botSlug={business.slug} consentSettings={(canReadConfig || canReadNotificationConfig) ? <>
        {canReadConfig && <ConsentFormSettings key={`consent-forms-${businessId}`} botId={businessId} disabled={!canUpdateConfig} />}
        {canReadNotificationConfig && <section className="mt-8 space-y-3" aria-label="Consent form link notification settings">
          <h2 className="text-lg font-bold text-slate-900">WhatsApp notifications</h2>
          <WhatsAppNotificationsPanel
            botId={businessId}
            readOnly={!can(user, "notification_configuration.update")}
            eventKeys={["consent_link"]}
          />
        </section>}
      </> : null} />}
      {activeTab === "chat-settings" && <MessageIdentitySettings key={`message-identity-${businessId}`} botId={businessId} disabled={!canUpdateConfig} />}
      {activeTab === "appointments" && <SchedulingTab botId={businessId} activeSection={workspace ? activeSchedulingSection : undefined} />}
      {activeTab === "schedulers" && <section className="space-y-4" aria-label="Schedulers">
        {canManageTriggers && <div role="tablist" aria-label="Scheduler sections" className="flex gap-2">
          {[["schedules", "Schedules"], ["trigger", "Trigger Settings"]].map(([key, label]) => (
            <button type="button" key={key} role="tab" id={`scheduler-${key}-tab`} aria-selected={schedulerSection === key}
              aria-controls={`scheduler-${key}-panel`} onClick={() => setSchedulerSection(key)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold ${schedulerSection === key ? "bg-[#096dea] text-white" : "border border-slate-200 text-slate-700"}`}>{label}</button>
          ))}
        </div>}
        {canManageTriggers && schedulerSection === "trigger"
          ? <div role="tabpanel" id="scheduler-trigger-panel" aria-labelledby="scheduler-trigger-tab"><TriggerSettings /></div>
          : <div role={canManageTriggers ? "tabpanel" : undefined} id="scheduler-schedules-panel" aria-labelledby={canManageTriggers ? "scheduler-schedules-tab" : undefined}>
            <SchedulesPanel key={businessId} botId={businessId} defaultTimezone={business?.timezone || (workspace ? "UTC" : "Asia/Kolkata")} />
          </div>}
      </section>}

      {/* TAB: Details */}
      {activeTab === "details" && (
        <form onSubmit={handleSaveDetails} className="space-y-6">
          {/* Section 3: AI Assistant Prompt */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
              <FileText size={16} className="text-[#096dea]" /> AI Assistant System Prompt
            </h3>
            <textarea
              readOnly={!canUpdateConfig}
              value={form.system_prompt}
              onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
              rows={6}
              placeholder="You are the official AI assistant for..."
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-mono focus:border-[#096dea] focus:ring-1 focus:ring-[#096dea] focus:outline-none leading-relaxed"
            />
          </div>

          {/* Section 4: Metadata JSON */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
              <FileText size={16} className="text-[#096dea]" /> Custom Metadata JSON
            </h3>
            <textarea
              readOnly={!canUpdateConfig}
              value={form.metadata_json}
              onChange={(e) => setForm((f) => ({ ...f, metadata_json: e.target.value }))}
              rows={3}
              placeholder="{}"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-mono focus:border-[#096dea] focus:ring-1 focus:ring-[#096dea] focus:outline-none leading-relaxed"
            />
          </div>

          <div className="flex justify-end pt-2">
            {canUpdateConfig && <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#0959bb] transition shadow-sm disabled:opacity-50"
            >
              <Save size={16} /> {saving ? "Saving Changes..." : "Save Bot Configuration"}
            </button>}
          </div>
        </form>
      )}

      {/* TAB: Flow */}
      {activeTab === "flow" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Bot Flow Configuration</h3>
            <p className="mt-1 text-xs text-slate-500">
              Define the bot flow as a JSON object and publish it for this business.
            </p>
          </div>
          <textarea
            readOnly={!canUpdateConfig}
            value={flowJson}
            onChange={(e) => setFlowJson(e.target.value)}
            rows={14}
            spellCheck="false"
            aria-label="Bot Flow Configuration"
            disabled={flowLoading}
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 font-mono text-xs leading-relaxed focus:border-[#096dea] focus:ring-1 focus:ring-[#096dea] focus:outline-none"
          />
          {flowLoading && <p className="text-xs text-slate-500">Loading active flow...</p>}
          <div className="flex justify-end">
            {canUpdateConfig && <button
              type="button"
              onClick={handlePublishFlow}
              disabled={flowSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0959bb] disabled:opacity-50"
            >
              <Save size={16} /> {flowSaving ? "Saving..." : "Save"}
            </button>}
          </div>
        </div>
      )}

      {/* TAB: Tools */}
      {activeTab === "tools" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">Tools</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Enabled function tools that the AI assistant can call for this business unit.
              </p>
            </div>
            <button
              onClick={() => setShowAddToolModal(true)}
              disabled={unlinkedTools.length === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0959bb] transition disabled:opacity-50"
            >
              <Plus size={16} /> Link Tool
            </button>
          </div>

          {bizTools.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              No tools linked yet. Click "+ Link Tool" to enable function calling for this business.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100">
                  <tr>
                    <th className="px-4 py-3">Tool Name</th>
                    <th className="px-4 py-3">Executor Type</th>
                    <th className="px-4 py-3">Enabled</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {bizTools.map((bt) => (
                    <tr key={bt.tool_id} className="hover:bg-slate-50/60 transition">
                      <td className="px-4 py-3.5 font-medium text-slate-900">{bt.name}</td>
                      <td className="px-4 py-3.5">
                        <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
                          {bt.executor_type}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={bt.enabled}
                          onChange={() => handleToggleTool(bt.tool_id, bt.enabled)}
                          className="h-4 w-4 rounded border-slate-300 text-[#096dea]"
                        />
                      </td>
                      <td className="px-4 py-3.5 text-right space-x-2">
                        <button
                          onClick={() => openEditTool(bt)}
                          className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 transition"
                        >
                          Edit Config
                        </button>
                        <button
                          onClick={() => handleRemoveTool(bt.tool_id)}
                          className="rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 transition"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Tool Modal */}
      {showAddToolModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-slate-200 space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Link Tool to Business</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Select Tool *
                </label>
                <select
                  value={addToolId}
                  onChange={(e) => setAddToolId(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:border-[#096dea] focus:outline-none bg-white"
                >
                  <option value="">-- Select Tool --</option>
                  {unlinkedTools.map((tool) => {
                    const toolId = tool.id ?? tool.tool_id;
                    return (
                    <option key={toolId} value={String(toolId)}>
                      {tool.name} ({tool.executor_type})
                    </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Configuration (JSON)
                </label>
                <textarea
                  value={addToolConfig}
                  onChange={(e) => setAddToolConfig(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddToolModal(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddTool}
                disabled={addToolSaving || !addToolId}
                className="rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0959bb] disabled:opacity-50 transition"
              >
                {addToolSaving ? "Linking..." : "Link Tool"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tool Modal */}
      {editingTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-slate-200 space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Edit Config — {editingTool.name}</h2>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="tool-enabled"
                  checked={editToolForm.enabled}
                  onChange={(e) => setEditToolForm((f) => ({ ...f, enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-[#096dea]"
                />
                <label htmlFor="tool-enabled" className="text-sm font-semibold text-slate-700">
                  Enable Tool for this Business
                </label>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Configuration (JSON)
                </label>
                <textarea
                  value={editToolForm.configuration}
                  onChange={(e) => setEditToolForm((f) => ({ ...f, configuration: e.target.value }))}
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2 font-mono text-xs focus:border-[#096dea] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditingTool(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditTool}
                disabled={editToolSaving}
                className="rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0959bb] disabled:opacity-50 transition"
              >
                {editToolSaving ? "Saving..." : "Save Configuration"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
